import React, { useMemo } from 'react';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { ExplorePageFailure } from 'apps/sigma2-ui/src/explore-section/components/ExplorePageFailure';
import { StreamlitCustomVizOutput } from 'libs/end-to-end/src/custom-viz/StreamlitCustomVizTypes';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CustomAppsSection } from 'apps/sigma2-ui/src/explore-section/components/CustomAppsPage';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { z } from 'zod';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';

export const ExternalStreamlitCard = ({
  result,
  status,
}: {
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}) => {
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();
  const { isLoading, data, hasFailed } = useMemo(
    () =>
      parseCustomVizResult({
        result,
        status,
        ResultZodType: StreamlitCustomVizOutput,
      }),
    [result, status],
  );

  if (hasFailed && !isLoading) {
    return <ExplorePageFailure />;
  }

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        w="full"
      >
        <CustomVizCircularSpinner />
      </Box>
    );
  }

  return (
    <>
      {selectedOrganizationId &&
        selectedDatabaseId &&
        selectedDatabaseRevisionId && (
          <CustomAppsSection
            organizationId={selectedOrganizationId}
            databaseId={selectedDatabaseId}
            databaseRevisionId={selectedDatabaseRevisionId}
            selectedAppId={data.streamlitAppId}
            customVizRequest={null}
          />
        )}
    </>
  );
};

export const ExternalStreamlitForPrototypeCard = ({
  customVizRequest,
  result,
  status,
  chart,
}: {
  customVizRequest: CustomVizRequest;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  chart?: DraftDashboardChart;
}) => {
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();
  const { isLoading, data, hasFailed } = useMemo(
    () =>
      parseCustomVizResult({
        result,
        status,
        ResultZodType: z.object({}),
      }),
    [result, status],
  );

  if (hasFailed && !isLoading) {
    return <ExplorePageFailure />;
  }

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        w="full"
      >
        <CustomVizCircularSpinner />
      </Box>
    );
  }

  return (
    <>
      {selectedOrganizationId &&
        selectedDatabaseId &&
        selectedDatabaseRevisionId &&
        chart &&
        chart.viewConfiguration.streamlitPrototypeAppId && (
          <CustomAppsSection
            organizationId={selectedOrganizationId}
            databaseId={selectedDatabaseId}
            databaseRevisionId={selectedDatabaseRevisionId}
            selectedAppId={chart.viewConfiguration.streamlitPrototypeAppId}
            customVizRequest={customVizRequest}
          />
        )}
    </>
  );
};
